import React, { useEffect, useRef, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  Grid,
  Typography,
  Breadcrumbs,
  Card,
  CardContent,
  Button,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  Box,
  CircularProgress,
} from "@mui/material";
import Link from "@mui/material/Link";
import axios from "axios";
import moment from "moment";
import Layout from "../../layout/Layout";
import { AlertMessage } from "../../components/AlertMessage";
import DiscussionThread from "../../components/DiscussionThread";
import {
  config,
  userData,
  paymentRequestDetailAPI,
  paymentRequestWorkflowAPI,
  paymentRequestProcessAPI,
} from "../../config";

// Web action hub for the new Verify -> Approve -> Pay chain + Reject/Query.
// Mirrors the mobile Detail hub: fetches /detail (which returns server-computed
// `can` flags), renders the allowed actions, and embeds the discussion thread.
const inr = (n) => "₹" + Number(n || 0).toLocaleString("en-IN");

const STATUS_META = {
  55: { label: "Pending", color: "warning" },
  500: { label: "Verified", color: "info" },
  56: { label: "Approved", color: "success" },
  57: { label: "Completed", color: "success" },
  58: { label: "Declined", color: "error" },
};

const PAYMENT_MODES = ["Cash", "Cheque", "UPI", "Bank Transfer"];

// What the payment reference is called per mode. Cash has none -- handing over
// notes produces no reference -- so the field is hidden for it entirely.
const TXN_FIELD = {
  Cash: null,
  Cheque: "Cheque number",
  UPI: "UPI reference",
  "Bank Transfer": "Transaction id",
};
const BANK_OPTIONS = ["IOB TECH TRF", "KVB TECH TRF", "KG CONS TRF", "KGF TRF"];

const Row = ({ label, value }) =>
  value === undefined || value === null || value === "" ? null : (
    <Grid container sx={{ py: 0.6 }}>
      <Grid item xs={5} sx={{ color: "#7d8f8e", fontSize: 13, fontWeight: 600 }}>
        {label}
      </Grid>
      <Grid item xs={7} sx={{ fontSize: 13.5, fontWeight: 600, color: "#14201f" }}>
        {value}
      </Grid>
    </Grid>
  );

export default function PaymentRequestReview() {
  const { id } = useParams();
  const navigate = useNavigate();
  const childRef = useRef();

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);

  // dialog: 'reject' | 'query' | 'approve' | 'pay' | null
  const [dialog, setDialog] = useState(null);
  const [text, setText] = useState("");
  const [mode, setMode] = useState("");
  const [bank, setBank] = useState("");
  const [txnId, setTxnId] = useState("");

  const post = (url, body) => {
    config.method = "POST";
    config.url = url;
    config.data = JSON.stringify(body);
    return axios(config);
  };

  const alert = (message, messageType) =>
    childRef.current.triggerAlert(JSON.stringify({ message, messageType }));

  const load = async () => {
    try {
      const res = await post(paymentRequestDetailAPI, {
        intPaymentRequestID: id,
        intActorID: userData.emp_id,
        intRoleID: userData.role_id,
      });
      setData(res.data && res.data.result ? res.data.result[0] : null);
    } catch (e) {
      console.log(e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const runAction = async (action, extra = {}) => {
    setBusy(true);
    try {
      const res = await post(paymentRequestWorkflowAPI, {
        txtAction: action,
        intPaymentRequestID: id,
        intActorID: userData.emp_id,
        ...extra,
      });
      if (res.data && res.data.success) {
        alert(res.data.message || "Updated", "success");
        setDialog(null);
        setText("");
        await load();
      } else {
        alert((res.data && res.data.message) || "Not allowed", "error");
      }
    } catch (e) {
      const msg = e && e.response && e.response.data && e.response.data.message;
      alert(msg || "Something went wrong", "error");
    } finally {
      setBusy(false);
    }
  };

  const onApprove = () => {
    if (data && data.over_limit) {
      setText("");
      setDialog("approve");
    } else {
      runAction("approve");
    }
  };

  const submitDialog = () => {
    const t = text.trim();
    if (dialog === "reject") return t ? runAction("reject", { txtReason: t }) : alert("Enter a reason", "error");
    if (dialog === "query") return t ? runAction("query", { txtBody: t }) : alert("Enter your query", "error");
    if (dialog === "approve") return t ? runAction("approve", { txtOverrideReason: t }) : alert("Enter an override reason", "error");
  };

  const submitPay = async () => {
    if (!mode) return alert("Select a payment mode", "error");
    if (mode === "Bank Transfer" && !bank) return alert("Select the bank", "error");
    setBusy(true);
    try {
      const res = await post(paymentRequestProcessAPI, {
        intType: 3,
        intPaymentRequestID: id,
        intAmount: data.amount,
        intProjectID: data.project_id,
        intContractorID: data.contractor_id || 0,
        intAccountantID: userData.emp_id,
        txtPaymentMode: mode === "Bank Transfer" ? bank : mode,
        // Never send a reference for a mode that has none (Cash).
        txtTransactionID: TXN_FIELD[mode] ? txnId : "",
        txtAccountantDescription: text,
        txtAccountantPaidDatetime: moment().format("YYYY-MM-DD HH:mm:ss"),
        intStatus: 57,
      });
      if (res.data) {
        alert("Payment recorded", "success");
        setDialog(null);
        await load();
      }
    } catch (e) {
      alert("Could not record payment", "error");
    } finally {
      setBusy(false);
    }
  };

  if (loading)
    return (
      <Layout>
        <Box sx={{ textAlign: "center", py: 6 }}>
          <CircularProgress />
        </Box>
      </Layout>
    );

  if (!data)
    return (
      <Layout>
        <Typography sx={{ p: 3 }}>Payment request not found.</Typography>
      </Layout>
    );

  const meta = STATUS_META[data.status_id] || STATUS_META[55];
  const can = data.can || {};
  const isPo = data.po_details && /with\s+(po|wo)/i.test(data.po_details);

  return (
    <Layout>
      <Grid className="employee-form bold-header">
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} lg={8}>
            <Typography className="page_title">Payment Request #{data.id}</Typography>
            <Breadcrumbs aria-label="breadcrumb">
              <Link underline="hover" color="inherit" href="/dashboard">
                Dashboard
              </Link>
              <Link underline="hover" color="inherit" onClick={() => navigate(-1)} sx={{ cursor: "pointer" }}>
                Payment Requests
              </Link>
              <Typography color="text.primary">Review</Typography>
            </Breadcrumbs>
          </Grid>
        </Grid>

        <div className="empty-space-20"></div>

        <Grid container spacing={2}>
          {/* Left: details + actions */}
          <Grid item xs={12} md={7}>
            <Card variant="outlined" sx={{ borderRadius: 2 }}>
              <CardContent>
                <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 1 }}>
                  <Typography sx={{ fontSize: 24, fontWeight: 800, color: "#17706e" }}>
                    {inr(data.amount)}
                  </Typography>
                  <Chip label={meta.label} color={meta.color} />
                </Box>
                {data.over_limit ? (
                  <Chip size="small" color="error" label="Over PO/WO limit" sx={{ mb: 1 }} />
                ) : null}

                <Row label="Project" value={data.project_name} />
                <Row label="Requested by" value={data.supervisor_name} />
                <Row label="Employee no" value={data.supervisor_number} />
                <Row label="Payment type" value={data.po_details} />
                {isPo ? <Row label="Order" value={data.po_name || "-"} /> : null}
                <Row label="Nature of expense" value={data.nature_of_expense} />
                <Row label="Description" value={data.employee_description} />
                {isPo && data.po_total > 0 ? (
                  <>
                    <Row label="PO total" value={inr(data.po_total)} />
                    <Row label="PO balance" value={inr(data.po_balance)} />
                  </>
                ) : null}
                {data.status_id === 57 ? (
                  <>
                    <Row label="Payment mode" value={data.payment_mode} />
                    <Row label="Transaction id" value={data.transaction_id} />
                  </>
                ) : null}
                {data.status_id === 58 && data.admin_description ? (
                  <Row label="Decline reason" value={data.admin_description} />
                ) : null}

                {/* Actions */}
                <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, mt: 2 }}>
                  {can.verify && (
                    <Button variant="contained" color="info" onClick={() => runAction("verify")}>
                      Verify
                    </Button>
                  )}
                  {can.approve && (
                    <Button variant="contained" color="success" onClick={onApprove}>
                      Approve
                    </Button>
                  )}
                  {can.pay && (
                    <Button
                      variant="contained"
                      color="secondary"
                      onClick={() => {
                        setMode("");
                        setBank("");
                        setTxnId("");
                        setText("");
                        setDialog("pay");
                      }}
                    >
                      Pay
                    </Button>
                  )}
                  {can.query && (
                    <Button variant="outlined" color="warning" onClick={() => { setText(""); setDialog("query"); }}>
                      Query
                    </Button>
                  )}
                  {can.reject && (
                    <Button variant="outlined" color="error" onClick={() => { setText(""); setDialog("reject"); }}>
                      Reject
                    </Button>
                  )}
                </Box>
              </CardContent>
            </Card>
          </Grid>

          {/* Right: discussion */}
          <Grid item xs={12} md={5}>
            <DiscussionThread
              module="payment_request"
              recordId={String(data.id)}
              recordLabel={`Payment request #${data.id}`}
              counterpartyId={data.supervisor_id}
            />
          </Grid>
        </Grid>
      </Grid>

      {/* Reason / query / override dialog */}
      <Dialog open={dialog === "reject" || dialog === "query" || dialog === "approve"} onClose={() => setDialog(null)} fullWidth maxWidth="sm">
        <DialogTitle>
          {dialog === "reject" ? "Reject — reason" : dialog === "query" ? "Raise a query" : "Over-limit — override reason"}
        </DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            fullWidth
            multiline
            minRows={3}
            placeholder={dialog === "query" ? "Ask the requester…" : "Enter a reason…"}
            value={text}
            onChange={(e) => setText(e.target.value)}
            sx={{ mt: 1 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialog(null)}>Cancel</Button>
          <Button variant="contained" onClick={submitDialog} disabled={busy}>
            {busy ? <CircularProgress size={20} /> : "Submit"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Pay dialog */}
      <Dialog open={dialog === "pay"} onClose={() => setDialog(null)} fullWidth maxWidth="sm">
        <DialogTitle>Record payment — {inr(data.amount)}</DialogTitle>
        <DialogContent>
          <TextField
            select fullWidth label="Payment mode" value={mode}
            onChange={(e) => { setMode(e.target.value); setBank(""); setTxnId(""); }}
            sx={{ mt: 1, mb: 2 }}
          >
            {PAYMENT_MODES.map((m) => (
              <MenuItem key={m} value={m}>{m}</MenuItem>
            ))}
          </TextField>
          {mode === "Bank Transfer" && (
            <TextField select fullWidth label="Bank" value={bank} onChange={(e) => setBank(e.target.value)} sx={{ mb: 2 }}>
              {BANK_OPTIONS.map((b) => (
                <MenuItem key={b} value={b}>{b}</MenuItem>
              ))}
            </TextField>
          )}
          {/* Cash has no reference to record, so no field for it. */}
          {mode && TXN_FIELD[mode] && (
            <TextField fullWidth label={TXN_FIELD[mode]} value={txnId} onChange={(e) => setTxnId(e.target.value)} sx={{ mb: 2 }} />
          )}
          <TextField fullWidth label="Description" multiline minRows={2} value={text} onChange={(e) => setText(e.target.value)} />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialog(null)}>Cancel</Button>
          <Button variant="contained" onClick={submitPay} disabled={busy}>
            {busy ? <CircularProgress size={20} /> : "Record payment"}
          </Button>
        </DialogActions>
      </Dialog>

      <AlertMessage ref={childRef} />
    </Layout>
  );
}
